/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { BoundToEdge } from '../../../component-classes';
import { deletePanelVents } from './delete-panel-vents';
import { Panel } from '../panel.class';
import { PanelItem } from '../../panel-items/panel-item.class';
import { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';
import { ReferenceMap } from '../../shared.types';
import { Vertex3 } from '../../../spatial-classes';

export type AddPanelVentsParams = {
	panel: Panel;
	refMap: ReferenceMap;
};

const holdsOnlyRuleManagedVents = (piGroup: PanelItemGroup): boolean => {
	return piGroup.items.children.length > 0 && piGroup.items.children.every((panelItem) => panelItem.item.category?.isVent && panelItem.ruleManaged);
};

// Vents always sit centered on their panel; a group left off-center by an earlier reposition hangs
// the vent off the edge of a narrow (reduced-depth) side panel (GitHub #451).
const centerVentGroup = (piGroup: PanelItemGroup): void => {
	if (piGroup.center.x !== 0) piGroup.center = new Vertex3({ dataIn: { x: 0 }});
};

export const addPanelVents = (params: AddPanelVentsParams): number => {
	// console.log('panels:helpers:-> addPanelVents', params);

	const { panel, refMap } = params;

	// resolve the island from the panel's own refMap so the surround material
	// is always available -- the event-level refMap.island is null when the
	// trigger fires at kitchen scope (e.g., surround_material.updated)
	const island = panel.refMap.island ?? refMap.island;

	// determine vent SKU based on surround material and panel position
	const surroundKey = island?.surround?.key ?? '';
	const isMok = surroundKey.startsWith('moks/');
	const ventSku = panel.isBackPanel
		? (isMok ? 'MOK-VENT-BACK' : 'VENT-BACK')
		: (isMok ? 'MOK-VENT-SIDE' : 'VENT-SIDE');

	let count = 0;

	// check for panelGroups containing rule-managed vents (ignore manually placed vents)
	const ventGroups = panel.panelItemGroups.children.filter((piGroup) => {
		return piGroup.items.children.filter((panelItem) => panelItem.item.category?.isVent && panelItem.ruleManaged).length > 0;
	});


	if (ventGroups.length === 0 && (panel.isSidePanel || panel.isBackPanel)) {
		// console.log('panels:helpers:-- addPanelVents', 'adding panel vents');
		count += 2;
		const piGroup = panel.panelItemGroups.new();
		const halfPanelHeight = panel.size.height / 2;

		const topVent = new PanelItem({ sku: ventSku, parent: piGroup });
		topVent.boundToEdge = BoundToEdge.Top;
		topVent.ruleManaged = true;
		topVent.center.y = -halfPanelHeight + (topVent.item.size.height / 2);

		const bottomVent = new PanelItem({ sku: ventSku, parent: piGroup });
		bottomVent.boundToEdge = BoundToEdge.Bottom;
		bottomVent.ruleManaged = true;
		bottomVent.center.y = halfPanelHeight - (bottomVent.item.size.height / 2);

		piGroup.items.children.push(topVent, bottomVent);
		centerVentGroup(piGroup);
	} else if (ventGroups.length === 1) {
		if (holdsOnlyRuleManagedVents(ventGroups[0])) centerVentGroup(ventGroups[0]);
		const panelVents = ventGroups[0].items.children.filter((panelItem) => panelItem.item.category?.isVent && panelItem.ruleManaged);
		const hasTopVent = panelVents.filter((vent) => vent.boundToEdge === BoundToEdge.Top).length > 0;
		const hasBottomVent = panelVents.filter((vent) => vent.boundToEdge === BoundToEdge.Bottom).length > 0;

		if (hasTopVent && hasBottomVent) {
			// console.log('panels:helpers:-- addPanelVents : %cpanel already has vents', 'color:green;', ventGroups);
		} else {
			const halfPanelHeight = panel.size.height / 2;
			if (!hasTopVent) {
				const vent = ventGroups[0].items.newFromSku(ventSku);
				vent.boundToEdge = BoundToEdge.Top;
				vent.ruleManaged = true;
				vent.center.y = -halfPanelHeight + (vent.item.size.height / 2);
				count++;
				// console.log('panels:helpers:-- addPanelVents : %cpanel missing top vent, vent added', 'color:green;', ventGroups);
			}
			if (!hasBottomVent) {
				const vent = ventGroups[0].items.newFromSku(ventSku);
				vent.boundToEdge = BoundToEdge.Bottom;
				vent.ruleManaged = true;
				vent.center.y = halfPanelHeight - (vent.item.size.height / 2);
				count++;
				// console.log('panels:helpers:-- addPanelVents : %cpanel missing bottom vent, vent added', 'color:green;', ventGroups);
			}
		}
	} else if (ventGroups.length > 1) {
		// console.log('panels:helpers:-- addPanelVents : %cpanel has to many vents, removing excess', 'color:green;', ventGroups);
		count -= deletePanelVents({ panel, refMap, groupExclusions: [ventGroups[0].id] });
	} else if (panel.label !== 'frontPanel') {
		// console.log('panels:helpers:-- addPanelVents : %csomething isn\'t right', 'color:red;background-color:yellow;', panel, ventGroups);
	}

	console.log('panels:helpers:<- addPanelVents', { count });
	return count;
};
